import { useEffect, useState } from 'react'
import { trackEvent } from '@/lib/analytics'

export interface SurveyOption {
  optionValue: string
  optionLabel: string
  displayOrder: number
}

export interface SurveyResult {
  optionValue: string
  optionLabel: string
  votes: number
  percentage: number
}

export interface SurveyTodayData {
  questionId: number
  question: string
  options: SurveyOption[]
  results: SurveyResult[]
  totalVotes: number
  hasRespondedToday: boolean
  currentStreak: number
  longestStreak: number
}

export interface SurveyHistoryItem {
  questionId: number
  question: string
  activeDate: string | null
  userAnswer: string | null
  userAnswerLabel: string | null
  results: SurveyResult[]
  totalVotes: number
}

export function useSurvey(userId: string) {
  const [data, setData] = useState<SurveyTodayData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    setLoading(true)
    fetch(`/api/survey/today?userId=${encodeURIComponent(userId)}&t=${Date.now()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.questionId === 'number') setData(d)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [userId])

  const submitResponse = async (optionValue: string): Promise<void> => {
    if (!data) return
    const res = await fetch('/api/survey/respond', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        questionId: data.questionId,
        optionValue,
      }),
    })
    const updated = res.ok ? await res.json() : {}
    trackEvent('survey_answered', { questionId: data.questionId, streak: updated.currentStreak })
    // Re-fetch so results include the user's own vote
    const fresh = await fetch(
      `/api/survey/today?userId=${encodeURIComponent(userId)}&t=${Date.now()}`,
    )
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
    if (fresh && typeof fresh.questionId === 'number') {
      setData(fresh)
    } else {
      setData((prev) =>
        prev
          ? {
              ...prev,
              hasRespondedToday: true,
              currentStreak: updated.currentStreak ?? prev.currentStreak,
              longestStreak: updated.longestStreak ?? prev.longestStreak,
            }
          : prev,
      )
    }
  }

  return { data, loading, submitResponse }
}
